import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismButton,
  PrismCheckbox,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import {
  type AccessRight,
  MIXED,
  type MixedRole,
  ROLE_OPTIONS_MAP,
} from 'lumen/utils/constants/users-constants';

import styles from './bulk-action-row.module.css';

interface BulkActionRowSignature {
  Args: {
    badgeColspan?: number;
    bulkBiBenchmarking: { checked: boolean; indeterminate: boolean };
    bulkRateAndMarketInsight: { checked: boolean; indeterminate: boolean };
    bulkRole: { isIndeterminate: boolean; value: AccessRight | MixedRole };
    deleteColspan?: number;
    isSelectAllActive?: boolean;
    numberOfSelectedRows: number;
    onBulkBiBenchmarkingToggle?: () => void;
    onBulkDelete?: () => void;
    onBulkRateAndMarketInsightToggle?: () => void;
    onBulkRoleChange: (option: Option) => unknown;
  };
}

export default class BulkActionRow extends Component<BulkActionRowSignature> {
  @service declare intl: Services['intl'];

  get badgeColspan(): number {
    return this.args.badgeColspan ?? 1;
  }

  get deleteColspan(): number {
    return this.args.deleteColspan ?? 2;
  }

  get roleOptions(): Option[] {
    return Object.entries(ROLE_OPTIONS_MAP).map(([key, translationKey]) => ({
      key,
      label: this.intl.t(translationKey),
    }));
  }

  get selectedRoleOption(): Option | undefined {
    if (this.args.bulkRole.value === MIXED) {
      return undefined;
    }

    return this.roleOptions.find(
      (option) => option.key === this.args.bulkRole.value,
    );
  }

  <template>
    <th class={{styles.cell}} colspan={{this.badgeColspan}}>
      {{#if @isSelectAllActive}}
        <PrismBadge
          @label={{t
            "settings.users.bulk.all_users_selected"
            count=@numberOfSelectedRows
          }}
          @type="info"
          data-test-id="bulk-selected-count"
        />
      {{else}}
        <PrismBadge
          @label={{t
            "settings.users.bulk.selected_properties_count"
            count=@numberOfSelectedRows
          }}
          @type="info"
          data-test-id="bulk-selected-count"
        />
      {{/if}}
    </th>
    <th class={{styles.cell}}>
      <div class={{styles.dropdown}}>
        <PrismSelectInput
          @isSmall={{true}}
          @onChange={{@onBulkRoleChange}}
          @options={{this.roleOptions}}
          @placeholder={{t "general.mixed"}}
          @unsafe_matchTriggerWidth={{true}}
          @value={{this.selectedRoleOption}}
        />
      </div>
    </th>
    <th class={{styles.cell}}>
      <PrismCheckbox
        @checked={{@bulkRateAndMarketInsight.checked}}
        @indeterminate={{@bulkRateAndMarketInsight.indeterminate}}
        @onChange={{@onBulkRateAndMarketInsightToggle}}
        data-test-id="bulk-rate-and-market-insight-checkbox"
      />
    </th>
    <th class={{styles.cell}}>
      <PrismCheckbox
        @checked={{@bulkBiBenchmarking.checked}}
        @indeterminate={{@bulkBiBenchmarking.indeterminate}}
        @onChange={{@onBulkBiBenchmarkingToggle}}
        data-test-id="bulk-bi-benchmarking-checkbox"
      />
    </th>
    <th class={{styles.cell}} colspan={{this.deleteColspan}}>
      <div class={{styles.delete}}>
        <PrismButton
          @buttonType="secondary"
          @icon="bin"
          @onClick={{@onBulkDelete}}
          @size="small"
          data-test-id="bulk-delete"
        />
      </div>
    </th>
  </template>
}
